import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';

import {
  FileDocumentOutlineIcon as IconDtbs,
  PenIcon as IconSignature,
  CalendarClockIcon as IconSignDate,
  TimelapseIcon as IconElapsedTime
} from 'mdi-react';
import _ from 'lodash';
import numeral from 'numeral';

import DBsignService from '../services/common/DBsignService';
import Iconized from './common/Iconized';
import MyTextField from './common/MyTextField';
import LabeledText from './common/LabeledText';
import ErrorInfo from './common/ErrorInfo';
import SignerInfo from './common/SignerInfo';

type Props = { classes: Object };

type State = {
  version: string,
  initSuccess: boolean,
  initTime: string,
  dtbs: string,
  signature: string,
  response?: Object,
  operationTime: string
};

class SignatureTest extends Component<Props, State> {
  state: State = {
    version: 'N/A',
    initSuccess: true,
    initTime: '0.0',
    dtbs: 'My dog has fleas.',
    signature: '',
    response: undefined,
    operationTime: ''
  };

  serviceDBsign = new DBsignService();

  componentDidMount() {
    console.log('componentDidMount');
    this.serviceDBsign.init(
      this.handleOnDBsignReady.bind(this),
      this.handleOnDBsignInitFailure.bind(this)
    );
  }

  handleOnDBsignReady = () => {
    const { version, initSuccess, initTimeMs } = this.serviceDBsign;
    const initTime = numeral(initTimeMs / 1000.0).format('0.000');
    this.setState({ version, initSuccess, initTime });
  };

  handleOnDBsignInitFailure = () => {
    console.log('handleOnDBsignInitFailure');
  };

  handleSignClick = () => {
    console.log('handleSignClick');

    // this.setState({
    //   response: {},
    //   signature: ''
    // });

    let nTimeStart = Date.now();

    let objState = {
      response: { ...this.state.response },
      signature: '',
      operationTime: ''
    };

    this.serviceDBsign.dbsign.signData(
      this.state.dtbs,
      function(objResult) {
        objState.operationTime = numeral((Date.now() - nTimeStart) / 1000.0).format('0.000');

        if (objResult) {
          delete objResult['DBS_ERROR_INFO'].CONTENT_TYPE;
          objState.response = objResult;
        }
        if (objResult['DBS_ERROR_INFO']['DBS_ERROR_VAL'] !== 0) {
          objState.signature = '';
        } else {
          objState.signature = objResult['RESULT_INFO']['DBS_SIGNATURE'];
        }

        this.setState(objState);
      }.bind(this)
    );
  };

  handleVerifyClick = () => {
    console.log('handleVerifyClick');

    // this.setState({
    //   response: {}
    // });

    let nTimeStart = Date.now();

    let objState = {
      response: { ...this.state.response },
      operationTime: ''
    };

    this.serviceDBsign.dbsign.verifySignature(
      this.state.dtbs,
      this.state.signature,
      function(objResult) {
        objState.operationTime = numeral((Date.now() - nTimeStart) / 1000.0).format('0.000');

        if (objResult) {
          delete objResult['DBS_ERROR_INFO'].CONTENT_TYPE;
          objState.response = objResult;
        }

        this.setState(objState);
      }.bind(this)
    );
  };

  render() {
    const { version, dtbs, initSuccess, initTime, signature, response, operationTime } = this.state;

    const { classes } = this.props;

    const iconProps = {
      className: classes.icon
    };

    const errorInfo: Object = _.get(response || {}, 'DBS_ERROR_INFO', {});
    const resultInfo: Object = _.get(response || {}, 'RESULT_INFO', {});
    const dbsSignerInfo: Object = _.get(resultInfo, 'DBS_SIGNER_INFO', {});
    const signerCert: Object = _.pickBy(
      _.get(dbsSignerInfo, 'CERT', {}),
      val => !_.isObject(val) && !_.isArray(val)
    );
    const signerInfo: Object = _.assign(dbsSignerInfo, signerCert);
    const signDate: string = _.get(dbsSignerInfo, 'SIGNING_TIME', '');

    return (
      <Card classes={{ root: classes.cardRoot }} raised>
        <CardHeader
          classes={{ root: classes.headerRoot, title: classes.headerTitle }}
          component="h1"
          title="DBsign Signature Test"
        />
        <CardContent>
          <Grid container wrap="nowrap" spacing={8}>
            <Grid item xs>
              <LabeledText label="DBsign Version:" name="version">
                {version}
              </LabeledText>
            </Grid>
            <Grid item xs>
              <LabeledText label="Initialization Success:" name="initSuccess">
                {initSuccess ? 'true' : 'false'}
              </LabeledText>
            </Grid>
            <Grid item xs>
              <LabeledText label="Initialization Time (sec):" name="initTime">
                {initTime}
              </LabeledText>
            </Grid>
          </Grid>
          <Divider />
          <Typography className={classes.sectionHeading} variant="title">
            Signature Test
          </Typography>
          <Iconized icon={<IconDtbs {...iconProps} />} iconCentered>
            <MyTextField
              className={classes.textField}
              label="Data To Sign"
              name="dtbs"
              onChange={({ currentTarget: { value } }) => {
                this.setState({ dtbs: value });
              }}
              value={dtbs}
              error={dtbs.length <= 0}
              required
            />
          </Iconized>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              className={classes.buttonSign}
              onClick={this.handleSignClick}
              disabled={dtbs.length <= 0}
            >
              Sign Data
            </Button>
            {signature.trim().length <= 0 ? null : (
              <Button
                variant="contained"
                color="secondary"
                className={classes.buttonVerify}
                onClick={this.handleVerifyClick}
              >
                Verify Signature
              </Button>
            )}
          </div>

          <Typography className={classes.sectionHeading} variant="title">
            Results
          </Typography>

          <ErrorInfo errorInfo={errorInfo} nvpDisplayProps={{ dividerAfter: false }} />
          <Divider className={classes.dividerText} />
          <Iconized icon={<IconSignature {...iconProps} />} iconCentered>
            <LabeledText
              label="Signature:"
              name="signature"
              textProps={{ className: classes.signatureText }}
            >
              {signature}
            </LabeledText>
          </Iconized>
          <Divider className={classes.dividerText} />
          <SignerInfo signerInfo={signerInfo} nvpDisplayProps={{ dividerAfter: false }} />
          <Divider className={classes.dividerText} />
          <Iconized icon={<IconSignDate {...iconProps} />} iconCentered>
            <LabeledText label="Sign Date:" name="signDate">
              {signDate}
            </LabeledText>
          </Iconized>
          <Divider className={classes.dividerText} />
          <Iconized icon={<IconElapsedTime {...iconProps} />} iconCentered>
            <LabeledText label="Sign or Verify Time (sec):" name="operationTime">
              {operationTime}
            </LabeledText>
          </Iconized>
          <Divider className={classes.dividerText} />
          <div style={{ textAlign: 'center' }}>
            <a href="https://www.dbsign.com/" target="_blank" rel="noopener noreferrer">
              <img
                alt="logo"
                className={classes.securedByDBsign}
                src="images/SecuredByDBsign.png"
              />
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }
}

const styles = ({
  spacing: { unit: spacing },
  palette: { primary: primaryColorInfo }
  //transitions,
  //breakpoints
}) => ({
  cardRoot: {
    marginTop: spacing * 0,
    marginBottom: spacing * 1,
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: spacing * 75,
    minWidth: spacing * 35
    //border: '1px solid blue',
  },
  headerRoot: {
    margin: 0,
    backgroundColor: primaryColorInfo.main,
    padding: spacing * 1.5
  },
  headerTitle: {
    color: primaryColorInfo.contrastText,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  sectionHeading: {
    marginTop: spacing * 3,
    marginBottom: spacing * 1
  },
  textField: {
    margin: spacing * 0
  },
  buttonContainer: {
    margin: spacing * 0,
    marginTop: spacing * 2,
    marginLeft: spacing * 4
  },
  buttonSign: {},
  buttonVerify: {
    marginLeft: spacing * 2
  },
  icon: {
    color: primaryColorInfo.main
  },
  signatureText: {
    wordWrap: 'break-word',
    overflow: 'auto',
    maxHeight: spacing * 10,
    boxSizing: 'border-box',
    marginRight: spacing * 4
  },
  dividerText: {
    marginLeft: spacing * 4,
    marginTop: spacing * 0,
    marginBottom: spacing * 2
  },
  securedByDBsign: {
    width: '175px',
    marginTop: spacing * 5,
    marginLeft: 'auto',
    marginRight: 'auto'
  }
});

export default withStyles(styles)(SignatureTest);
